import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageToggle } from './LanguageToggle';

export const LandingPage: React.FC = () => {
  const { t } = useLanguage();
  
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-400 to-sky-100 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-10 left-[5%] w-24 h-24 bg-white rounded-full opacity-20"></div>
        <div className="absolute top-32 right-[10%] w-40 h-40 bg-yellow-300 rounded-full opacity-20"></div>
        <div className="absolute bottom-20 left-[15%] w-32 h-32 bg-orange-300 rounded-full opacity-15"></div>
        <div className="absolute top-[40%] right-[5%] w-20 h-20 bg-green-300 rounded-full opacity-20"></div>
        <div className="absolute bottom-40 right-[20%] w-28 h-28 bg-white rounded-full opacity-25"></div>
      </div>
      {/* Header with Language Toggle and Navigation */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-4">
        <Link 
          to="/setup"
          className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg border-2 border-slate-900"
        >
          ⚙️ Setup
        </Link>
        <LanguageToggle />
      </div>
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 to-black">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl sm:text-7xl font-black text-white mb-6">
              {t.hero.title}<span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">{t.hero.titleSuffix}</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 font-semibold mb-4">
              {t.hero.subtitle}
            </p>
            {t.hero.tagline && (
              <p className="text-lg text-gray-300 max-w-2xl mx-auto font-medium border-l-4 border-yellow-400 pl-4">
                {t.hero.tagline}
              </p>
            )}
          </motion.div>
        </div>
      </div>

      {/* Current Focus Section */}
      <div className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white">
        {/* Section divider */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400"></div>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-pink-600 mb-4">
              {t.currentWork.title}
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 mx-auto rounded-full"></div>
          </motion.div>

          {/* WhatsApp Automation Feature */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white to-sky-50 rounded-3xl p-8 sm:p-12 border-4 border-slate-900 shadow-[8px_8px_0px_0px_rgba(251,191,36,1)] relative"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block bg-gradient-to-br from-green-400 to-emerald-500 rounded-full p-4 mb-6 border-4 border-slate-900 ring-4 ring-yellow-400 ring-offset-4">
                  <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </div>
                <h3 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600 mb-4">
                  {t.currentWork.whatsapp.title}
                </h3>
                <p className="text-slate-700 mb-6 leading-relaxed font-medium">
                  {t.currentWork.whatsapp.description}
                </p>
                <ul className="space-y-3 text-slate-700 font-medium">
                  {t.currentWork.whatsapp.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-600 mr-2 text-xl font-bold">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border-4 border-slate-900 border-t-8 border-t-green-500">
                  <div className="space-y-4">
                    {/* Mock WhatsApp Messages */}
                    <div className="bg-white rounded-xl p-3 max-w-[80%] border-2 border-slate-900 border-l-4 border-l-sky-500">
                      <p className="text-sm text-slate-800 font-medium">{t.currentWork.whatsapp.mockMessages.customer1}</p>
                    </div>
                    <div className="bg-gradient-to-r from-green-400 to-emerald-400 rounded-xl p-3 max-w-[80%] ml-auto border-2 border-slate-900 shadow-md">
                      <p className="text-sm text-white font-medium">{t.currentWork.whatsapp.mockMessages.bot1}</p>
                    </div>
                    <div className="bg-white rounded-xl p-3 max-w-[80%] border-2 border-slate-900 border-l-4 border-l-sky-500">
                      <p className="text-sm text-slate-800 font-medium">{t.currentWork.whatsapp.mockMessages.customer2}</p>
                    </div>
                    <div className="bg-gradient-to-r from-green-400 to-emerald-400 rounded-xl p-3 max-w-[80%] ml-auto border-2 border-slate-900 shadow-md">
                      <p className="text-sm text-white font-medium">{t.currentWork.whatsapp.mockMessages.bot2}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-sky-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-pink-600 mb-4">
              {t.expertise.title}
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 mx-auto rounded-full"></div>
          </motion.div>

          <motion.div 
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {t.expertise.services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="bg-white rounded-2xl p-6 border-4 border-slate-900 shadow-[4px_4px_0px_0px_rgba(251,191,36,1)] hover:shadow-[6px_6px_0px_0px_rgba(251,191,36,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-300 relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-400 opacity-20 rounded-bl-full group-hover:scale-150 transition-transform"></div>
                <div className="text-4xl mb-4 relative z-10">{service.icon}</div>
                <h3 className="text-xl font-black text-slate-900 mb-2 relative z-10">{service.title}</h3>
                <p className="text-slate-700 font-medium relative z-10">{service.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Technology Stack */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-sky-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-pink-600 mb-4">
              {t.techStack.title}
            </h2>
            <div className="w-24 h-1 bg-purple-500 mx-auto mb-8"></div>
            <div className="flex flex-wrap justify-center gap-4">
              {t.techStack.technologies.map((tech) => (
                <span 
                  key={tech}
                  className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-slate-900 rounded-full text-sm border-2 border-slate-900 font-bold hover:from-yellow-300 hover:to-orange-300 transition-all"
                >
                  {tech}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-sky-50 to-sky-100 relative">
        {/* Decorative corner elements */}
        <div className="absolute top-0 left-0 w-32 h-32 border-t-8 border-l-8 border-orange-400 rounded-tl-3xl"></div>
        <div className="absolute bottom-0 right-0 w-32 h-32 border-b-8 border-r-8 border-pink-400 rounded-br-3xl"></div>
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-purple-600 mb-6">
              {t.cta.title}
            </h2>
            <p className="text-xl text-slate-700 font-semibold">
              {t.cta.subtitle}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t-4 border-slate-900 py-8 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400 font-medium">
            {t.footer.copyright.replace('{year}', new Date().getFullYear().toString())}
          </p>
        </div>
      </footer>
    </div>
  );
};